<template>
    <v-autocomplete
        v-model="selectedCompany"
        :items="companies"
        :loading="loading"
        item-title="display"
        item-value="id"
        :label="computedLabel"
        clearable
        @update:search="onSearchInput"
        @update:modelValue="onSelect"
        :no-data-text="
            (search || '').length < 3
                ? $t('addPaymentIncident.typeThreeChars')
                : $t('addPaymentIncident.noResults')
        "
    >
        <template #selection>
            {{ selectedDisplay }}
        </template>
    </v-autocomplete>
</template>

<script>
import api from "@/api";

export default {
    name: "IncidentCompanySelect",
    props: {
        modelValue: {
            type: [String, Number],
            default: null,
        },
    },
    emits: ["update:modelValue"],
    data() {
        return {
            search: "",
            companies: [],
            loading: false,
            selectedDisplay: "",
        };
    },
    computed: {
        selectedCompany: {
            get() {
                return this.modelValue;
            },
            set(id) {
                this.$emit("update:modelValue", id);
            },
        },
        /**
         * Dynamically choose the label based on the current route name.
         */
        computedLabel() {
            const routeName = this.$route.name;
            // assuming your "other companies" route is named "OtherCompanyIncidents"
            if (routeName === "OtherCompanyIncidents") {
                return this.$t("addPaymentIncident.filterCompanyName");
            } else {
                return this.$t("addPaymentIncident.companyName");
            }
        },
    },
    watch: {
        /**
         * Whenever the parent changes the selected company,
         * ensure the item exists in the dropdown, or load it.
         * @param {Number|String|null} newId
         */
        async modelValue(newId) {
            if (newId && !this.companies.some((c) => c.id === newId)) {
                await this.loadCompanyById(newId);
            } else if (!newId) {
                this.selectedDisplay = "";
            }
        },
    },
    /**
     * On mount, if a company is already selected, ensure it's loaded for display.
     */
    async mounted() {
        if (
            this.selectedCompany &&
            !this.companies.some((c) => c.id === this.selectedCompany)
        ) {
            await this.loadCompanyById(this.selectedCompany);
        }
    },
    methods: {
        /**
         * Helper to format a company object for the dropdown.
         * @param {Object} company - Company object from API
         * @returns {Object} - Company with id, name, and display string
         */
        formatCompany(company) {
            return {
                id: company.id,
                name: company.name,
                display: [
                    company.name,
                    company.VATNumber,
                    [company.street, company.number].filter(Boolean).join(" "),
                    company.city,
                    company.country,
                ]
                    .filter(Boolean)
                    .join(" | "),
            };
        },
        /**
         * Ensures the selected company is present and shown in the input,
         * even if not loaded by search. Used when a pre-selection exists.
         * @param {String|Number} companyId - The company ID to ensure in dropdown
         * @returns {Promise<void>}
         */
        async loadCompanyById(companyId) {
            // If already in the list, select it
            if (this.companies.some((c) => c.id === companyId)) {
                this.onSelect(companyId);
                return;
            }
            this.loading = true;
            try {
                const resp = await api.get(`/companies/${companyId}/`);
                const companyObj = this.formatCompany(resp.data);
                // Prepend it to the list (avoid duplicates)
                this.companies = [
                    companyObj,
                    ...this.companies.filter((co) => co.id !== companyId),
                ];
                this.onSelect(companyId);
            } catch (err) {
                console.error("Error fetching selected company:", err);
            } finally {
                this.loading = false;
            }
        },
        /**
         * Called whenever the user selects a company from the dropdown.
         * Finds the matching object in `companies` and caches its display text.
         * @param {string|number|null} id
         */
        onSelect(id) {
            // Protect against empty array
            const foundCompany = Array.isArray(this.companies)
                ? this.companies.find((c) => c.id === id)
                : null;
            this.selectedDisplay = foundCompany ? foundCompany.display : "";
        },
        /**
         * Handler for search-input updates.
         * Normalizes the value to a string, updates `search`,
         * and triggers a fetch when length ≥ 3.
         * @param {string|null} val
         */
        onSearchInput(val) {
            // Coerce to string so we never call .length on null
            const query = typeof val === "string" ? val : "";
            this.search = query;

            if (query.length >= 3) {
                this.fetchCompanies(query);
            } else if (!this.selectedCompany) {
                this.companies = [];
            }
        },
        /**
         * Fetch matching companies from the API by search query.
         * Populates `companies` with `{ id, display }` objects,
         * and refreshes the selected display if already chosen.
         * @param {string} query
         * @returns {Promise<void>}
         */
        async fetchCompanies(query) {
            this.loading = true;
            try {
                const resp = await api.get("/companies/", {
                    params: { search: query },
                });
                const list = resp.data?.data || resp.data || [];
                this.companies = list.map(this.formatCompany);
                // If already selected, refresh the display
                if (this.selectedCompany) {
                    this.onSelect(this.selectedCompany);
                }
            } catch (err) {
                console.error("Error fetching companies:", err);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
